import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, Button, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { PublicAPI } from '../../api';
import { logosURL } from '../../config';
import { withLanguage } from '../common/Texts';
import { getText } from '../common/MultiLanguageText';
import {
    validateDynamicAnswers,
    coerceDynamicAnswersForSubmit,
    fieldsApplicableToReply,
} from '../../utils/dynamicFields';
import { validateGenericId } from '../../utils/genericId';
import withRouter from '../../utils/withRouter';

import QuizPrivacy from './QuizPrivacy';
import QuizWelcome from './QuizWelcome';
import QuizPersonalData from './QuizPersonalData';
import QuizQuestion from './QuizQuestion';
import QuizResult from './QuizResult';
import { PAGE_BG, FONT } from './tokens';

const INITIAL_TIME = 2100;
const EXTRA_TIME = 300;
const MAX_TIME = INITIAL_TIME + EXTRA_TIME;

const STEP = {
    LOADING: 'loading',
    PRIVACY: 'privacy',
    WELCOME: 'welcome',
    PERSONAL: 'personal',
    QUESTIONS: 'questions',
    SUCCESS: 'success',
    ERROR: 'error',
    LEAVE: 'leave',
};

// Debe coincidir con NO_CREDITS_MESSAGE en backend/TalentSense/talentapp/api/common.py
const ACCESS_DENIED_MESSAGE = 'Lo siento, no es posible realizar esta operación. Por favor contactá a tu referente de Recursos Humanos para que gestione el acceso correspondiente.';

function selectRandomQuestions(questions) {
    if (!questions || questions.length === 0) return [];
    const byGroup = questions.reduce((acc, q) => {
        const k = q.group;
        if (!acc[k]) acc[k] = [];
        acc[k].push(q);
        return acc;
    }, {});
    const keys = Object.keys(byGroup);
    const group = byGroup[keys[Math.floor(Math.random() * keys.length)]];
    return [...group].sort((a, b) => a.order - b.order);
}

function getPrivacyPolicy(policy, language) {
    if (!policy) return '';
    const lang = (language || 'es').toLowerCase();
    return policy[lang] || '';
}

function getMLText(obj, attr, language) {
    return getText(obj, attr, language);
}

function Quiz({ location }) {
    const language = useSelector(s => s.user.language);
    const t = useCallback((key) => withLanguage(language)(key), [language]);

    const [step, setStep] = useState(STEP.LOADING);
    // Primera pantalla mostrada: solo ahí se permite elegir el idioma.
    const [firstStep, setFirstStep] = useState(null);
    const [company, setCompany] = useState({});
    const [questionnaire, setQuestionnaire] = useState({});
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [dynamic_fields, setDynamicFields] = useState([]);
    const [dynamic_answers, setDynamicAnswers] = useState({});
    const [dynamic_errors, setDynamicErrors] = useState({});
    const [positions, setPositions] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [business_units, setBusinessUnits] = useState([]);
    const [internal_groups, setInternalGroups] = useState([]);
    const [id_label, setIdLabel] = useState('');
    const [logo_cli, setLogoCli] = useState('');
    const quizHash = new URLSearchParams(location.search).get('id') || '';
    const [reply_id, setReplyId] = useState(0);
    const [resultMessage, setResultMessage] = useState('');
    const [isAccessDenied, setIsAccessDenied] = useState(false);

    const [nombre, setNombre] = useState('');
    const [apellido, setApellido] = useState('');
    const [dob, setDob] = useState(dayjs());
    const [user_generic_id, setUserGenericId] = useState('');
    const [posicion, setPosicion] = useState(0);
    const [department, setDepartment] = useState(0);
    const [business_unit, setBusinessUnit] = useState(0);
    const [internal_group, setInternalGroup] = useState(0);
    const [errors, setErrors] = useState({
        dob: '', nombre: '', apellido: '', posicion: '',
        department: '', user_generic_id: '', business_unit: '', internal_group: '',
    });

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [secondsLeft, setSecondsLeft] = useState(INITIAL_TIME);
    // El aviso de "quedan 5 minutos" es derivado: se muestra al bajar de EXTRA_TIME
    // y desaparece cuando el usuario lo cierra o suma tiempo.
    const [fiveMinAcked, setFiveMinAcked] = useState(false);
    const show5mins = !fiveMinAcked && secondsLeft > 0 && secondsLeft <= EXTRA_TIME;

    const startTimeRef = useRef(null);
    const intervalRef = useRef(null);
    const replyPromiseRef = useRef(null);
    const autoSubmittedRef = useRef(false);

    // El idioma se usa para elegir la pantalla inicial, pero cambiarlo después no
    // debe volver a pedir el cuestionario ni reiniciar el paso: no va en las deps.
    useEffect(() => {
        if (!quizHash) return;

        PublicAPI.get(`/questionnaireHash/${quizHash}`).then(res => {
            const c = res.data.company;
            setCompany({ id: c.id, name: c.name });
            setPositions(c.positions);
            setDepartments(c.departments);
            setBusinessUnits(c.business_units);
            setInternalGroups(c.grupos_internos);
            setIdLabel(c.id_label);
            if (c.logofile) setLogoCli(logosURL + c.logofile);

            const qs = selectRandomQuestions(res.data.questions);
            setQuestions(qs);
            setAnswers(qs.map(q => ({ answer: '', question: q.id })));

            const dfields = fieldsApplicableToReply(c.dynamic_fields, null);
            setDynamicFields(dfields);
            const dans = {};
            dfields.forEach(f => { dans[f.key] = ''; });
            setDynamicAnswers(dans);

            setQuestionnaire(res.data);

            const policy = getPrivacyPolicy(res.data.privacy_policy, language);
            const welcomeMsg = getMLText(res.data, 'welcome_message', language);
            const initialStep = policy ? STEP.PRIVACY : welcomeMsg ? STEP.WELCOME : STEP.PERSONAL;
            setFirstStep(initialStep);
            setStep(initialStep);
        }).catch(err => {
            const msg = err?.response?.data?.message || err?.message || 'Error de conexión';
            setResultMessage(msg);
            setIsAccessDenied(msg === ACCESS_DENIED_MESSAGE);
            setStep(STEP.ERROR);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizHash]);

    useEffect(() => {
        if (step !== STEP.QUESTIONS) return;
        return () => clearInterval(intervalRef.current);
    }, [step]);

    function handleAutoSubmit() {
        if (autoSubmittedRef.current) return;
        autoSubmittedRef.current = true;
        clearInterval(intervalRef.current);
        handleSubmit(true);
    }

    useEffect(() => {
        if (secondsLeft !== 0) return;
        handleAutoSubmit();
        // handleAutoSubmit cierra sobre las respuestas del render actual: se
        // reevalúa en cada render, no hace falta declararlo como dependencia.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft]);

    function startTimer() {
        startTimeRef.current = new Date();
        intervalRef.current = setInterval(() => {
            setSecondsLeft(prev => {
                const elapsed = new Date() - startTimeRef.current;
                return elapsed > MAX_TIME * 1000 ? 0 : prev - 1;
            });
        }, 1000);
    }

    function validateField(name, value, errs) {
        const out = { ...errs };
        switch (name) {
            case 'dob': {
                const edad = dayjs().diff(dayjs(value), 'year');
                out.dob = (value === '' || edad < 18 || edad > 100) ? t('age_validation') : '';
                break;
            }
            case 'nombre':
                out.nombre = value.length < 2 ? t('name_validation') : '';
                break;
            case 'apellido':
                out.apellido = value.length < 2 ? t('surname') + ' ' + t('is_mandatory') : '';
                break;
            case 'user_generic_id':
                if ((value || '').length < 1) {
                    out.user_generic_id = id_label + ' ' + t('is_mandatory');
                } else {
                    const idErr = validateGenericId(value);
                    out.user_generic_id = idErr ? t(idErr) : '';
                }
                break;
            case 'posicion':
                out.posicion = value == 0 ? ' ' : '';
                break;
            case 'department':
                out.department = value == 0 ? ' ' : '';
                break;
            case 'business_unit':
                out.business_unit = value == 0 ? ' ' : '';
                break;
            case 'internal_group':
                out.internal_group = value === 0 ? ' ' : '';
                break;
        }
        return out;
    }

    function handleChange(event) {
        const { name, value } = event.target;
        const newErrors = validateField(name, value, errors);
        setErrors(newErrors);
        switch (name) {
            case 'nombre': setNombre(value); break;
            case 'apellido': setApellido(value); break;
            case 'user_generic_id': setUserGenericId(value); break;
            case 'posicion': setPosicion(value); break;
            case 'department': setDepartment(value); break;
            case 'business_unit': setBusinessUnit(value); break;
            case 'internal_group': setInternalGroup(value); break;
        }
    }

    function handleDateChange(newDob) {
        setErrors(prev => validateField('dob', newDob, prev));
        setDob(newDob);
    }

    const handleDynamicChange = (key) => (event) => {
        const value = event.target.value;
        setDynamicAnswers(prev => ({ ...prev, [key]: value }));
        setDynamicErrors(prev => { const next = { ...prev }; delete next[key]; return next; });
    };

    function handlePersonalNext() {
        let errs = { ...errors };
        errs = validateField('dob', dob, errs);
        errs = validateField('nombre', nombre, errs);
        errs = validateField('apellido', apellido, errs);
        if (positions.length > 0) errs = validateField('posicion', posicion, errs);
        if (departments.length > 0) errs = validateField('department', department, errs);
        if (business_units.length > 0) errs = validateField('business_unit', business_unit, errs);
        if (id_label) errs = validateField('user_generic_id', user_generic_id, errs);
        if (internal_groups.length > 0) errs = validateField('internal_group', internal_group, errs);

        const { valid: dynValid, errors: dynErrors } = validateDynamicAnswers(
            dynamic_fields, dynamic_answers, (k) => t(k), language
        );
        setDynamicErrors(dynErrors);
        setErrors(errs);

        const formValid = Object.values(errs).every(v => v === '');
        if (!formValid || !dynValid) return;

        postQuiz();
        startTimer();
        setStep(STEP.QUESTIONS);
        setCurrentQuestion(0);
    }

    function postQuiz() {
        const born = dayjs(dob);
        const edad = dayjs().diff(born, 'year');
        const data = {
            age: edad,
            dob: born.format('YYYY-MM-DD'),
            name: `${nombre} ${apellido}`,
            department,
            questionnaire: questionnaire.id,
            position: posicion,
            business_unit,
            user_generic_id,
            status: 'S',
        };
        if (internal_group > 0) data.grupo_interno = internal_group;
        if (dynamic_fields.length > 0) {
            data.dynamic_answers = coerceDynamicAnswersForSubmit(dynamic_fields, dynamic_answers);
        }
        replyPromiseRef.current = PublicAPI.post(`/replyHash/${quizHash}/`, data)
            .then(res => {
                setReplyId(res.data.reply_id);
                return res.data.reply_id;
            })
            .catch(err => {
                const msg = err?.response?.data?.message || err?.message || 'Error';
                setResultMessage(msg);
                setIsAccessDenied(msg === ACCESS_DENIED_MESSAGE);
                setStep(STEP.ERROR);
                return 0;
            });
    }

    function handleAnswerChange(event, index) {
        const val = event.target.value;
        if (val.length > 5000) return;
        for (let i = 0; i < val.length - 3; i++) {
            if (val[i] === val[i + 1] && val[i + 1] === val[i + 2] && val[i + 2] === val[i + 3]) return;
        }
        setAnswers(prev => {
            const next = [...prev];
            next[index] = { ...next[index], answer: val };
            return next;
        });
    }

    async function handleSubmit(auto) {
        clearInterval(intervalRef.current);
        const duration_seconds = Math.floor((new Date() - startTimeRef.current) / 1000);
        let oneIncomplete = false;
        for (let i = 0; i < questions.length; i++) {
            if (answers[i].answer.length < questions[i].min_length) {
                if (!auto) {
                    alert(t('min_text_required'));
                    return;
                }
                oneIncomplete = true;
            }
        }
        // El POST inicial de la respuesta puede no haber resuelto todavía; esperamos
        // su reply_id para no hacer PUT a /replyPut/0/.
        const rid = reply_id || (replyPromiseRef.current ? await replyPromiseRef.current : 0);
        if (!rid) return;
        const data = {
            status: oneIncomplete ? 'I' : 'C',
            answers,
            duration_seconds,
        };
        PublicAPI.put(`/replyPut/${rid}/`, data)
            .then(res => {
                if (auto && oneIncomplete) {
                    setResultMessage(t('end_time_over') + res.data.id);
                } else {
                    setResultMessage(String(res.data.id));
                }
                setStep(STEP.SUCCESS);
            })
            .catch(err => {
                const msg = err?.response?.data?.message || err?.message || 'Error';
                setResultMessage(msg);
                setIsAccessDenied(msg === ACCESS_DENIED_MESSAGE);
                setStep(STEP.ERROR);
            });
    }

    function handleReturn() {
        window.location.reload();
    }

    // Derivados del idioma actual: se recalculan al cambiar el idioma con el
    // selector del header, sin quedar fijados al idioma de carga.
    const welcomeMsg = getMLText(questionnaire, 'welcome_message', language) || '';
    const privacyPolicy = getPrivacyPolicy(questionnaire.privacy_policy, language);
    const getMLTextBound = (obj) => getMLText(obj, 'name', language);

    if (step === STEP.LOADING) {
        return <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: PAGE_BG, fontFamily: FONT }} />;
    }

    if (step === STEP.PRIVACY) {
        return (
            <QuizPrivacy
                policyText={privacyPolicy}
                title={t('privacy_policy_title')}
                showLanguage={firstStep === STEP.PRIVACY}
                onAccept={() => welcomeMsg ? setStep(STEP.WELCOME) : setStep(STEP.PERSONAL)}
                t={t}
            />
        );
    }

    if (step === STEP.WELCOME) {
        return (
            <QuizWelcome
                companyName={company.name}
                logoUrl={logo_cli}
                welcomeHtml={welcomeMsg}
                showLanguage={firstStep === STEP.WELCOME}
                onBegin={() => setStep(STEP.PERSONAL)}
                onLeave={() => setStep(STEP.LEAVE)}
                t={t}
            />
        );
    }

    if (step === STEP.PERSONAL) {
        return (
            <QuizPersonalData
                errors={errors}
                dynamic_errors={dynamic_errors}
                nombre={nombre} apellido={apellido} dob={dob} user_generic_id={user_generic_id}
                posicion={posicion} department={department} business_unit={business_unit} internal_group={internal_group}
                positions={positions} departments={departments} business_units={business_units} internal_groups={internal_groups}
                dynamic_fields={dynamic_fields} dynamic_answers={dynamic_answers}
                id_label={id_label}
                showLanguage={firstStep === STEP.PERSONAL}
                onChange={handleChange}
                onDateChange={handleDateChange}
                onDynamicChange={handleDynamicChange}
                onNext={handlePersonalNext}
                getMLText={getMLTextBound}
                language={language}
                t={t}
            />
        );
    }

    if (step === STEP.QUESTIONS && questions.length > 0) {
        const questionsForDisplay = questions.map(q => ({
            ...q,
            question_text: getMLText(q, 'question', language),
        }));
        return (
            <>
                <QuizQuestion
                    questions={questionsForDisplay}
                    questionIndex={currentQuestion}
                    questionCount={questions.length}
                    answers={answers}
                    minLengths={questions.map(q => q.min_length)}
                    secondsLeft={secondsLeft}
                    onAnswerChange={handleAnswerChange}
                    onNavigate={setCurrentQuestion}
                    onFocusQuestion={setCurrentQuestion}
                    onSubmit={() => handleSubmit(false)}
                    t={t}
                />
                <Dialog open={show5mins}>
                    <DialogContent sx={{ pt: 3 }}>
                        <DialogContentText>
                            <Typography variant="h6">{t('five_mine_left')}</Typography>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setFiveMinAcked(true);
                                setSecondsLeft(prev => prev + EXTRA_TIME);
                            }}
                        >
                            {t('add_five_min')}
                        </Button>
                        <Button onClick={() => setFiveMinAcked(true)} autoFocus>
                            {t('received_successfully')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    if (step === STEP.SUCCESS || step === STEP.ERROR || step === STEP.LEAVE) {
        return (
            <QuizResult
                variant={step === STEP.SUCCESS ? 'success' : step === STEP.LEAVE ? 'leave' : 'error'}
                message={resultMessage}
                soleMessage={isAccessDenied}
                onReturn={handleReturn}
                t={t}
            />
        );
    }

    return null;
}

export default withRouter(Quiz);
